import React, { useEffect, useState } from "react";
import { FileInput, Plus, Check, X, Trash2, Sparkles } from "lucide-react";
import api from "../lib/api";
import { Card, PageHeader, PageLoader, Badge, Button, Modal, Select } from "../lib/ui";
import { useToast } from "../lib/toast";
import { fmtRp, fmtNum, fmtUnit } from "../lib/constants";
import { useT } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";

const STATUS_TONE = { PENDING_REVIEW: "warn", CONFIRMED: "success", REJECTED: "mute" };
const STATUS_LABEL = { PENDING_REVIEW: "Pending Review", CONFIRMED: "Confirmed", REJECTED: "Rejected" };

export default function InvoiceIntake() {
  const { t } = useT();
  const toast = useToast();
  const { isRole } = useAuth();
  const canWrite = isRole("SUPER_ADMIN", "CENTRAL_PURCHASING", "WAREHOUSE");
  const [list, setList] = useState(null);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ supplier_id: "", invoice_number: "", invoice_date: "", items: [] });

  const load = () => api.get("/pending-receivings").then(({ data }) => setList(data));
  useEffect(() => {
    load();
    api.get("/products").then(({ data }) => setProducts(data.filter((p) => p.track_inventory !== false)));
    api.get("/suppliers").then(({ data }) => setSuppliers(data));
  }, []);

  if (!list) return <PageLoader />;

  const addLine = () => setForm((f) => ({ ...f, items: [...f.items, { product_id: "", quantity: "", unit_cost: "" }] }));
  const setLine = (idx, key, val) => setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, [key]: val } : it)) }));
  const rmLine = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const total = form.items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_cost) || 0), 0);

  const submit = async () => {
    const items = form.items.filter((it) => it.product_id && Number(it.quantity) > 0)
      .map((it) => ({ product_id: it.product_id, quantity: Number(it.quantity), unit_cost: Number(it.unit_cost) || 0 }));
    if (items.length === 0) return toast.error(t("Add at least one item"));
    setSaving(true);
    try {
      await api.post("/pending-receivings", { ...form, source: "manual", items });
      toast.success(t("Invoice saved"));
      setOpen(false); setForm({ supplier_id: "", invoice_number: "", invoice_date: "", items: [] }); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
    setSaving(false);
  };

  const confirm = async (pr) => {
    try { await api.post(`/pending-receivings/${pr.id}/confirm`); toast.success(t("Received into warehouse")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
  };
  const reject = async (pr) => {
    try { await api.post(`/pending-receivings/${pr.id}/reject`); toast.success(t("Rejected")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
  };

  return (
    <div className="animate-fade-up">
      <PageHeader title={t("Invoice Intake")} subtitle={t("Receive supplier invoices into the central warehouse")} icon={FileInput}>
        {canWrite && <Button onClick={() => { setForm({ supplier_id: "", invoice_number: "", invoice_date: "", items: [{ product_id: "", quantity: "", unit_cost: "" }] }); setOpen(true); }} data-testid="new-invoice"><Plus size={18} /> {t("New Invoice")}</Button>}
      </PageHeader>

      <Card className="mb-4 flex items-start gap-3 border-primary/20 bg-primary-light/40 p-4">
        <Sparkles size={18} className="mt-0.5 shrink-0 text-primary" />
        <p className="text-sm text-ink-soft" data-testid="intake-future-note">{t("Manual entry today. This is the entry point for future WhatsApp invoice upload & receipt OCR — confirming auto-updates inventory cost.")}</p>
      </Card>

      {list.length === 0 ? (
        <Card className="p-10 text-center text-ink-mute" data-testid="intake-empty">{t("No invoices yet")}</Card>
      ) : (
        <div className="space-y-3">
          {list.map((pr) => (
            <Card key={pr.id} className="p-4" data-testid={`intake-row-${pr.ref_no}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-sm font-bold text-ink">{pr.ref_no}</span>
                    <Badge tone={STATUS_TONE[pr.status] || "mute"}>{t(STATUS_LABEL[pr.status] || pr.status)}</Badge>
                    <Badge tone="info">{t("Source")}: {pr.source}</Badge>
                  </div>
                  <p className="mt-1 text-sm text-ink-soft">{pr.supplier_name}{pr.invoice_number ? ` · ${pr.invoice_number}` : ""}</p>
                  <p className="text-xs text-ink-mute">{pr.items.length} {t("items")} · {fmtRp(pr.total_cost)} · {(pr.created_at || "").slice(0, 16).replace("T", " ")}</p>
                </div>
                {canWrite && pr.status === "PENDING_REVIEW" && (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => confirm(pr)} data-testid={`confirm-${pr.ref_no}`}><Check size={15} /> {t("Confirm & Receive")}</Button>
                    <Button size="sm" variant="outline" onClick={() => reject(pr)} data-testid={`reject-${pr.ref_no}`}><X size={15} /> {t("Reject")}</Button>
                  </div>
                )}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {pr.items.map((it, i) => (
                  <span key={i} className="rounded-lg bg-stone-100 px-2.5 py-1 text-xs text-ink-soft">
                    {it.product_name} · {fmtNum(it.quantity)} {fmtUnit(it.unit)} @ {fmtRp(it.unit_cost)}
                  </span>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} size="lg" title={t("New Invoice")}
        footer={<><Button variant="outline" onClick={() => setOpen(false)}>{t("Cancel")}</Button><Button onClick={submit} loading={saving} data-testid="save-invoice">{t("Save Invoice")}</Button></>}>
        <div className="grid gap-3 sm:grid-cols-2">
          <Select label={t("Supplier")} value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })} data-testid="inv-supplier">
            <option value="">{t("Select supplier...")}</option>
            {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </Select>
          <div><label className="cf-label">{t("Invoice Number")}</label><input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} className="cf-input" data-testid="inv-number" /></div>
        </div>
        <div className="mt-4 mb-2 flex items-center justify-between">
          <label className="cf-label mb-0">{t("Items")}</label>
          <button type="button" onClick={addLine} className="rounded-lg border border-line px-2.5 py-1 text-xs font-semibold text-ink-soft hover:bg-stone-50" data-testid="inv-add-line"><Plus size={13} className="inline" /> {t("Add Item")}</button>
        </div>
        <div className="space-y-2">
          {form.items.map((it, idx) => (
            <div key={idx} className="grid grid-cols-12 items-end gap-2" data-testid={`inv-line-${idx}`}>
              <div className="col-span-6">
                <Select value={it.product_id} onChange={(e) => setLine(idx, "product_id", e.target.value)} data-testid={`inv-line-product-${idx}`}>
                  <option value="">{t("Product")}...</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </Select>
              </div>
              <div className="col-span-2"><input type="number" placeholder={t("Qty")} value={it.quantity} onChange={(e) => setLine(idx, "quantity", e.target.value)} className="cf-input" data-testid={`inv-line-qty-${idx}`} /></div>
              <div className="col-span-3"><input type="number" placeholder={t("Unit Cost")} value={it.unit_cost} onChange={(e) => setLine(idx, "unit_cost", e.target.value)} className="cf-input" data-testid={`inv-line-cost-${idx}`} /></div>
              <div className="col-span-1"><button type="button" onClick={() => rmLine(idx)} className="rounded-lg p-2 text-danger-text hover:bg-danger-light"><Trash2 size={15} /></button></div>
            </div>
          ))}
        </div>
        <div className="mt-3 flex justify-end text-sm font-semibold text-ink">{t("Total")}: {fmtRp(total)}</div>
      </Modal>
    </div>
  );
}
